import { forwardRef, type ButtonHTMLAttributes, type CSSProperties } from 'react'

/**
 * The one close control for the game: a clean diagonal X with rounded ends on a subtle round backing, so it reads as
 * a close button on every screen. The tap area is twice the old size (easy on touch screens and in Tesla mode, where
 * buttons and icons keep fixed sizes), while the X itself is only a little larger than before so it never looks
 * oversized. Callers pass the sizes the old icon used; the scaling lives here.
 */
export const CLOSE_SCALE = 2
/** How much bigger the X glyph is than the old icon (the tap area uses CLOSE_SCALE). */
export const CLOSE_GLYPH_SCALE = 1.25

/** The X glyph. `size` is the final pixel size. */
export function CloseGlyph({ size = 20, strokeWidth = 2.5, className }: { size?: number; strokeWidth?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" data-glyph="close-x" className={className}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  )
}

export interface CloseButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Accessible name. Defaults to "Close". */
  label?: string
  /** The old icon size in px; the X draws at CLOSE_GLYPH_SCALE times this. */
  glyph?: number
  /** The old square tap target in px; the tap area is CLOSE_SCALE times this. Leave out when CSS sizes the button. */
  box?: number
  strokeWidth?: number
}

export const CloseButton = forwardRef<HTMLButtonElement, CloseButtonProps>(function CloseButton(
  { label = 'Close', glyph = 16, box, strokeWidth, className = '', style, type = 'button', ...rest }, ref,
) {
  const size: CSSProperties | undefined = box ? { width: box * CLOSE_SCALE, height: box * CLOSE_SCALE } : undefined
  const g = Math.round(glyph * CLOSE_GLYPH_SCALE)
  const disc = g + 16
  return (
    <button ref={ref} type={type} aria-label={label} data-close-button=""
      className={`group inline-flex shrink-0 items-center justify-center focus-visible:shadow-none focus-visible:outline-none ${className}`} style={{ ...size, ...style }} {...rest}>
      <span data-close-backing="" style={{ width: disc, height: disc }}
        className="flex items-center justify-center rounded-full bg-black/40 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.22)] transition-colors group-hover:bg-black/60 group-active:bg-black/70 group-focus-visible:shadow-[0_0_0_2px_#0c1013,0_0_0_4px_rgba(224,165,46,0.9)]">
        <CloseGlyph size={g} strokeWidth={strokeWidth} />
      </span>
    </button>
  )
})
